import { useEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import clinicAPI from "../services/clinicApi";
import { updateTenant } from "../store/authSlice";
import {
  applyPortalBrandingToRoot,
  buildClientHeroTheme,
  clubBrandingSource,
  heroBackgroundStyle,
  mergeClubPortalBranding,
  portalGlobalCssVars,
} from "../utils/clientHeroTheme";

/**
 * Единая персонализация клуба: фирменный цвет, обложка шапки, шрифт.
 * @param {"aa"|"ca"} cssPrefix — админ или клиентское приложение
 * @param {object|null} extraBranding — например trainer_branding с dashboard
 * @param {boolean} refreshFromApi — подтянуть свежие настройки с сервера (админ)
 * @param {boolean} active — применять брендинг (false на загрузочном экране)
 */
export default function useClubPortalBranding(
  cssPrefix = "aa",
  extraBranding = null,
  refreshFromApi = false,
  active = true,
) {
  const dispatch = useDispatch();
  const tenant = useSelector((state) => state.auth.tenant);

  useEffect(() => {
    if (!refreshFromApi || !tenant?.id) return undefined;
    let cancelled = false;
    clinicAPI
      .getTenantSettings()
      .then((res) => {
        if (cancelled || !res?.tenant) return;
        dispatch(
          updateTenant({
            portal_accent_color: res.tenant.portal_accent_color,
            portal_cover_url: res.tenant.portal_cover_url,
            portal_font_family: res.tenant.portal_font_family,
            name: res.tenant.name,
            logo_url: res.tenant.logo_url,
          }),
        );
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [refreshFromApi, tenant?.id, dispatch]);

  const branding = useMemo(
    () => mergeClubPortalBranding(tenant, extraBranding) || clubBrandingSource(tenant),
    [
      tenant?.portal_accent_color,
      tenant?.portal_cover_url,
      tenant?.portal_font_family,
      extraBranding?.portal_accent_color,
      extraBranding?.portal_cover_url,
      extraBranding?.portal_font_family,
    ],
  );

  const theme = useMemo(() => buildClientHeroTheme(branding), [branding]);

  const shellStyle = useMemo(() => portalGlobalCssVars(theme), [theme]);

  const heroStyle = useMemo(() => heroBackgroundStyle(theme), [theme]);

  useEffect(() => {
    if (!active) return undefined;
    applyPortalBrandingToRoot(theme);
    return undefined;
  }, [theme, active]);

  return { theme, branding, shellStyle, heroStyle };
}
